import type { AgentHarness, HarnessToolDefinition } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import type { AgentMode, TaskContext } from "@project/shared";
import { buildCommonTools, buildForceUpdateTaskStatusTool } from "./common-tools.js";
import { buildPmTools, buildUpdateTaskTool, buildHandoffTool } from "./pm-tools.js";
import { buildDiscoveryTools } from "./discovery-tools.js";
import { buildGlossaryTools } from "./project-tools.js";
import {
  buildCodeReviewTools,
  buildPublishReviewGuideTool,
  resolveGitHeadSha,
} from "./code-review-tools.js";

// Re-export helpers
export { textResult, imageBlock } from "./helpers.js";

// ── Mode-based tool selection ─────────────────────────────────────────

function getTaskModeTools(agentMode: AgentMode | undefined, connection: AgentConnection) {
  // Building is included so auto tasks (which boot straight into building)
  // can document their plan into the card via update_task_plan as they work.
  // Chat is included because saving a plan is how a chat card escalates into a
  // real dev task: the first plan identifies the card, moves it to InProgress,
  // and unlocks its PR path.
  if (
    agentMode === "discovery" ||
    agentMode === "auto" ||
    agentMode === "building" ||
    agentMode === "chat"
  ) {
    return [buildUpdateTaskTool(connection)];
  }
  return [];
}

function getModeTools(
  agentMode: AgentMode | undefined,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
) {
  if (config.mode === "pack") {
    // Pack runners orchestrate children regardless of the agentMode axis:
    // subtask CRUD + start_child_cloud_build/stop_child_build/
    // approve_and_merge_pr are their whole job (they never build code).
    return buildPmTools(connection, { includePackTools: true });
  }
  if (config.mode === "task") return getTaskModeTools(agentMode, connection);

  switch (agentMode) {
    case "building":
      return context?.isParentTask ? buildPmTools(connection, { includePackTools: true }) : [];
    case "review":
    case "auto":
    case "discovery":
    case "help":
      return buildPmTools(connection, {
        includePackTools: !!context?.isParentTask,
      });
    default:
      return config.mode === "pm" ? buildPmTools(connection, { includePackTools: false }) : [];
  }
}

/**
 * The PR guide is authored by the builder as part of opening the PR (and
 * refreshed on each push), so publish_review_guide rides building/auto leaf
 * tasks — not review mode. Parent (pack) cards never open code PRs.
 */
function buildPrGuideToolsFor(
  effectiveMode: AgentMode | undefined,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
): HarnessToolDefinition[] {
  const isLeafBuild = effectiveMode === "building" || effectiveMode === "auto";
  return isLeafBuild && !context?.isParentTask
    ? [
        buildPublishReviewGuideTool(connection, {
          resolveHeadSha: () => resolveGitHeadSha(config.workspaceDir),
        }),
      ]
    : [];
}

// ── Tool assembly ─────────────────────────────────────────────────────

/**
 * Hot protocol tools preloaded into the model's prompt instead of deferred
 * behind ToolSearch. A 24h fleet transcript audit (2026-07-11) found ~350
 * ToolSearch calls/day, nearly all `select:`-loading these same conveyor
 * tools — every session paid 1–8 round trips just to load its own protocol
 * tools (and several wasted an extra call on bare names). Marking them
 * `alwaysLoad` eliminates those round trips on both harnesses.
 *
 * Keep this the intersection of "called by virtually every session in the
 * mode that exposes it" — the long tail (attachments, dependency CRUD,
 * per-test checklist edits) stays deferred so the prompt stays small. Names
 * not exposed in the current mode are simply absent from the built list, so
 * the set intersects with mode gating for free.
 */
export const ALWAYS_LOADED_TOOLS: ReadonlySet<string> = new Set([
  // Every mode/session
  "post_to_chat",
  "read_task_chat",
  "get_task",
  "get_current_plan",
  "set_manual_tests",
  "create_pull_request",
  // Planning/auto/building
  "update_task_plan",
  "update_task_properties",
  // Building/auto — the PR guide is published as part of opening the PR
  "publish_review_guide",
  // Review mode
  "approve_code_review",
  "request_code_changes",
  // Pack/parent orchestration — a 12h fleet audit (2026-07-14) found a pack
  // runner re-ToolSearching this exact set on EVERY wake (~28 redundant round
  // trips in one session). The whole orchestration loop (list → promote/assign
  // → fire → merge/stop) is called by virtually every pack session.
  "list_subtasks",
  "update_subtask",
  "start_child_cloud_build",
  "stop_child_build",
  "approve_and_merge_pr",
]);

/**
 * Mode-scoped promotions from the 72h fleet baseline (2026-07-24, 321
 * sessions): the top remaining `select:` loads after ALWAYS_LOADED_TOOLS
 * landed, hot only in the contexts that actually use them so the prompt cost
 * isn't paid fleet-wide. upload_attachment led at 56 loads/72h (the UI
 * screenshot/recording guidance made it de facto hot in building sessions),
 * create_suggestion 32, get_attachment 14, get_execution_logs 12 (pack
 * parents inspecting children), list_manual_tests 9, create_follow_up_task 8.
 * force_update_task_status (21 loads) stays deferred on purpose — ToolSearch
 * friction is a feature for the emergency status override.
 */
export const BUILDING_PROMOTED_TOOLS: ReadonlySet<string> = new Set([
  "upload_attachment",
  "get_attachment",
  "create_suggestion",
  "create_follow_up_task",
  "list_manual_tests",
]);
export const REVIEW_PROMOTED_TOOLS: ReadonlySet<string> = new Set(["create_suggestion"]);
export const PACK_PROMOTED_TOOLS: ReadonlySet<string> = new Set(["get_execution_logs"]);

/** Glossary tools travel with every card that knows its project. */
function glossaryToolsFor(
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
): HarnessToolDefinition[] {
  return context?.projectId
    ? buildGlossaryTools(connection, context.projectId, config.taskId, config.workspaceDir)
    : [];
}

function promotedToolsFor(
  effectiveMode: AgentMode | undefined,
  isPack: boolean,
): ReadonlySet<string> {
  const names = new Set<string>();
  // Auto boots straight into building, so it gets the building promotions.
  if (effectiveMode === "building" || effectiveMode === "auto") {
    for (const name of BUILDING_PROMOTED_TOOLS) names.add(name);
  }
  if (effectiveMode === "review") {
    for (const name of REVIEW_PROMOTED_TOOLS) names.add(name);
  }
  if (isPack) {
    for (const name of PACK_PROMOTED_TOOLS) names.add(name);
  }
  return names;
}

function withAlwaysLoad(
  tools: HarnessToolDefinition[],
  promoted: ReadonlySet<string>,
): HarnessToolDefinition[] {
  return tools.map((tool) =>
    ALWAYS_LOADED_TOOLS.has(tool.name) || promoted.has(tool.name)
      ? { ...tool, alwaysLoad: true }
      : tool,
  );
}

/** Collect all tools for the current mode as harness-neutral definitions. */
export function buildConveyorTools(
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
  agentMode?: AgentMode,
): HarnessToolDefinition[] {
  const effectiveMode = agentMode ?? context?.agentMode ?? undefined;

  const commonTools = buildCommonTools(connection, config);
  const modeTools = getModeTools(effectiveMode, connection, config, context);

  // Building included so auto tasks (booted straight into building) can
  // refine title/story points via update_task_properties while they work.
  // Chat included so a chat card can refine its own title.
  const discoveryTools =
    effectiveMode === "discovery" ||
    effectiveMode === "auto" ||
    effectiveMode === "building" ||
    effectiveMode === "chat"
      ? buildDiscoveryTools(connection)
      : [];

  // Code review verdict tools available in review mode
  const codeReviewTools = effectiveMode === "review" ? buildCodeReviewTools(connection) : [];

  const prGuideTools = buildPrGuideToolsFor(effectiveMode, connection, config, context);

  // Mid-conversation handoff is a Dev Manager (pm runner) capability — offered
  // while it is still planning (discovery/auto) so it can hand implementation
  // off to a difficulty-sized implementer agent.
  const handoffTools =
    config.mode === "pm" && (effectiveMode === "discovery" || effectiveMode === "auto")
      ? [buildHandoffTool(connection)]
      : [];

  const emergencyTools = [buildForceUpdateTaskStatusTool(connection)];

  // The project glossary travels with every card: read the vocabulary
  // (list_tags/get_tag) and maintain it (update_tag, card-stamped revisions).
  const glossaryTools = glossaryToolsFor(connection, config, context);

  const isPack = config.mode === "pack" || Boolean(context?.isParentTask);
  // create_pull_request stays in the chat toolset: a chat card that saves a
  // plan becomes a real dev task and needs it. The gate is dynamic rather than
  // structural — tool-access.ts denies the call until a plan is saved — so the
  // tool does not have to be re-added mid-session.
  return withAlwaysLoad(
    [
      ...commonTools,
      ...modeTools,
      ...discoveryTools,
      ...codeReviewTools,
      ...prGuideTools,
      ...handoffTools,
      ...glossaryTools,
      ...emergencyTools,
    ],
    promotedToolsFor(effectiveMode, isPack),
  );
}

// ── MCP server factory ────────────────────────────────────────────────

// oxlint-disable-next-line typescript/explicit-function-return-type
export function createConveyorMcpServer(
  harness: AgentHarness,
  connection: AgentConnection,
  config: AgentRunnerConfig,
  context?: TaskContext,
  agentMode?: AgentMode,
) {
  return harness.createMcpServer({
    name: "conveyor",
    tools: buildConveyorTools(connection, config, context, agentMode),
  });
}
